import React, { useState } from 'react';

const AboutUs = () => {
  const [activeTab, setActiveTab] = useState('about');

  const tabs = [
    { id: 'about', label: 'About Us' },
    { id: 'vision', label: 'Vision & Mission' },
    { id: 'services', label: 'Our Services' },
    { id: 'why', label: 'Why Choose Us' }
  ];

  return (
    <div className="max-w-6xl mx-auto p-4 bg-white">
      {/* Header Section with Logo and Intro */}
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold text-blue-800 mb-2">IndiaHiring.com</h1>
        <p className="text-xl text-gray-600">Empowering MSMEs with Smart Hiring Solutions</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap border-b border-gray-200 mb-8">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`px-4 py-2 font-medium text-sm md:text-base transition-colors duration-200 mr-1 ${
              activeTab === tab.id
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-blue-600'
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="tab-content transition-all duration-300 ease-in-out">
        {/* About Us Tab */}
        {activeTab === 'about' && (
          <div className="fade-in">
            <h2 className="text-2xl font-bold text-blue-800 mb-4">About Us</h2>
            <div className="bg-blue-50 p-6 rounded-lg shadow-sm mb-6">
              <p className="text-gray-800 leading-relaxed">
                IndiaHiring.com is a leading job portal committed to empowering Micro, Small, and Medium Enterprises (MSMEs) in India. We specialize in providing MSMEs with the best recruitment solutions by connecting them with the most suitable employees. Our services go beyond just hiring; we focus on employee training, self-development, and preparation, ensuring businesses have a skilled and motivated workforce. Additionally, we offer expert guidance to help businesses create and implement the best HR policies that foster sustainable growth, making us a one-stop solution for both recruitment and HR development needs.
              </p>
            </div>
          </div>
        )}

        {/* Vision & Mission Tab */}
        {activeTab === 'vision' && (
          <div className="fade-in">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-blue-50 p-6 rounded-lg shadow-sm">
                <h2 className="text-2xl font-bold text-blue-800 mb-4">Vision Statement</h2>
                <p className="text-gray-800 leading-relaxed">
                  To be the trusted partner for MSMEs in India, empowering them with cutting-edge hiring solutions, innovative HR policies, and a culture of continuous growth, ensuring sustainable success for businesses and their employees.
                </p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg shadow-sm">
                <h2 className="text-2xl font-bold text-blue-800 mb-4">Our Mission</h2>
                <p className="text-gray-800 leading-relaxed">
                  "To empower MSMEs in India by providing innovative hiring solutions, tailored HR policies, and continuous learning opportunities, fostering a culture of growth and sustainability that ensures long-term success for both businesses and their employees."
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="fade-in">
            <h2 className="text-2xl font-bold text-blue-800 mb-6">Core Services</h2>
            
            <div className="mb-8 bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
              <div className="bg-blue-700 p-4">
                <h3 className="text-xl font-bold text-white">1. Tailored Employee Recruitment Solutions</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700 mb-4">
                  IndiaHiring.com offers a comprehensive recruitment platform designed to connect MSMEs with the best-fit candidates. Our smart and efficient process ensures that the most suitable employees are hired based on skill sets, cultural fit, and business needs.
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Customized Job Matching: Using AI-powered technology to match employers with candidates based on their specific job requirements.</li>
                  <li>Industry-Specific Talent Pool: Access to a wide range of candidates specialized in various MSME sectors.</li>
                  <li>End-to-End Hiring Support: From job posting, candidate screening, interviews, to selection, we handle all steps to make hiring easy and efficient.</li>
                </ul>
              </div>
            </div>
            
            <div className="mb-8 bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
              <div className="bg-blue-700 p-4">
                <h3 className="text-xl font-bold text-white">2. Employee Training & Self-Development Programs</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700 mb-4">
                  To ensure the workforce is equipped with the necessary skills for success, IndiaHiring.com offers specialized training and development programs. These programs are designed to enhance both technical and soft skills, improving performance and boosting employee potential.
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Skill Enhancement: Training on job-specific skills and industry trends, ensuring employees stay competitive.</li>
                  <li>Leadership & Career Development: Programs to foster leadership qualities, team management, and career advancement.</li>
                  <li>Personal Development: Encouraging employees to grow beyond their job roles with workshops on time management, communication skills, and emotional intelligence.</li>
                </ul>
              </div>
            </div>
            
            <div className="mb-8 bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
              <div className="bg-blue-700 p-4">
                <h3 className="text-xl font-bold text-white">3. HR Policy Creation & Consulting</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700 mb-4">
                  We provide expert consulting services to help MSMEs create and implement effective HR policies that support long-term employee satisfaction and organizational growth. Our HR solutions focus on creating a positive, productive, and ethical work environment.
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Tailored HR Policies: Develop policies aligned with your business values, goals, and legal compliance.</li>
                  <li>Employee Engagement & Retention: Create policies that promote a motivated and loyal workforce, ensuring sustainable growth.</li>
                  <li>Well-Being and Inclusivity: Design policies that address work-life balance, employee wellness, diversity, and inclusion in the workplace.</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
              <div className="bg-blue-700 p-4">
                <h3 className="text-xl font-bold text-white">4. Preparation for Future Success</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700 mb-4">
                  IndiaHiring.com goes beyond traditional hiring and HR services by preparing organizations for long-term success. We help MSMEs plan for the future with forward-thinking strategies that nurture growth, enhance productivity, and ensure adaptability in an evolving business landscape.
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Future Workforce Planning: Guidance on hiring trends, future skill needs, and strategic workforce planning.</li>
                  <li>Organizational Development: Advice on building a strong company culture and creating an adaptable work environment.</li>
                  <li>Sustainability & Growth: Offering strategies to help businesses scale while maintaining ethical, responsible, and sustainable practices.</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Why Choose Us Tab */}
        {activeTab === 'why' && (
          <div className="fade-in">
            <h2 className="text-2xl font-bold text-blue-800 mb-6">Why Choose IndiaHiring.com?</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                  <span className="text-blue-600 text-2xl font-bold">1</span>
                </div>
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Comprehensive Support for MSMEs</h3>
                <p className="text-gray-700">
                  We understand the challenges MSMEs face and offer customized solutions designed to help you succeed in recruitment, HR policies, and employee development.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                  <span className="text-blue-600 text-2xl font-bold">2</span>
                </div>
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Expert HR Guidance</h3>
                <p className="text-gray-700">
                  With a focus on creating sustainable HR policies, we ensure that your workforce remains motivated, productive, and engaged.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                  <span className="text-blue-600 text-2xl font-bold">3</span>
                </div>
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Employee Growth-Focused Approach</h3>
                <p className="text-gray-700">
                  Our training programs focus not only on technical skills but also on personal and professional development, leading to a more fulfilled and skilled workforce.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                  <span className="text-blue-600 text-2xl font-bold">4</span>
                </div>
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Cutting-Edge Technology</h3>
                <p className="text-gray-700">
                  We leverage the latest technologies to optimize recruitment, training, and HR policy development, ensuring the most efficient and effective solutions.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                  <span className="text-blue-600 text-2xl font-bold">5</span>
                </div>
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Sustainable Growth Support</h3>
                <p className="text-gray-700">
                  Our long-term strategies ensure your business thrives through continuous development, retention of top talent, and the adoption of forward-thinking practices.
                </p>
              </div>
            </div>
            
            <div className="mt-10 text-center">
              <p className="text-lg font-semibold text-blue-800">
                Visit IndiaHiring.com today to take the next step towards a brighter, more sustainable future for your business and your employees.
              </p>
              <button className="mt-4 px-8 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm">
                Get Started Now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AboutUs;